import React, { useEffect, useState } from 'react';
import {
	SypacButton,
	SypacIcon,
	SypacInput,
	SypacText,
} from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { DaysSelect } from '../../../../components/ProductLocation/Selectors/Days/DaysSelect';
import { HoursSelect } from '../../../../components/ProductLocation/Selectors/Hours/HoursSelect';
import { Geo } from '../../../../interfaces/geo.interface';
import { VariantInterface } from '../../../../components/ProductLocation/ProductLocation.interface';
import {
	LocationDropdownProps,
	NewStoreCreateProps,
	StoreFormValues,
	TextFieldName,
} from '../productStores.interface';
import {
	CreateStoresParams,
	ProductService,
} from '../../../../services/product.services';
import { toastVariant } from '../../../../components/ToastVariant/toastVariant';
import { GeoService } from '../../../../services/geo.services';
import {
	addressWithoutCityCountry,
	cityCountryOnly,
} from '../../../../utils/string.util';
import StoreSearchPin from '../../../../assets/StoreSearchPin';
import { cn } from '../../../../lib/utils';
import Close from '../../../../assets/Close';
import HighlightText from '../../../../components/HighlightText/HighlightText';

const NewStoreCreate: React.FC<NewStoreCreateProps> = ({
	onClose,
	onChange,
	locations,
	setStoreLocation,
	storeLocation,
	setStoreName,
	isEditing,
	storeData,
	isReverseGeoLocation,
	onClearReverseGeoFlag,
}) => {
	const { t } = useTranslate();
	const [locationResults, setLocationResults] = useState<boolean>(false);
	const [pickupDays, setPickupDays] = useState<VariantInterface[]>([
		{
			title: t('newStoreCreate.selectWorkingDays', 'Select working days'),
			variant: 'days',
		},
	]);
	const [workHours, setWorkHours] = useState<VariantInterface[]>([
		{
			title: t('newStoreCreate.selectWorkingHours', 'Select working hours'),
			variant: 'hours',
		},
	]);

	const daysAndHoursSubmenu = [
		{ title: t('productLocation.monday', 'Monday'), variant: 'monday' },
		{ title: t('productLocation.tuesday', 'Tuesday'), variant: 'tuesday' },
		{
			title: t('productLocation.wednesday', 'Wednesday'),
			variant: 'wednesday',
		},
		{ title: t('productLocation.thursday', 'Thursday'), variant: 'thursday' },
		{ title: t('productLocation.friday', 'Friday'), variant: 'friday' },
		{ title: t('productLocation.saturday', 'Saturday'), variant: 'saturday' },
		{ title: t('productLocation.sunday', 'Sunday'), variant: 'sunday' },
	];

	const daysMenu = [
		{
			title: t('productLocation.businessDays', 'Business days'),
			variant: 'business_days',
		},
		{ title: t('productLocation.fullWeek', 'Full week'), variant: 'full_week' },
		{
			title: t('productLocation.custom', 'Custom'),
			variant: 'custom',
			submenu: daysAndHoursSubmenu,
		},
	];

	const hoursMenu = [
		{ title: t('productLocation.fullTime', 'Full Time'), variant: 'full_time' },
		{
			title: t('productLocation.fromHoursToHours', 'From 9:00 to 18:00'),
			variant: 'working_hours',
		},
		{
			title: t('productLocation.custom', 'Custom'),
			variant: 'custom',
			submenu: daysAndHoursSubmenu,
		},
	];

	const formik = useFormik<StoreFormValues>({
		initialValues: {
			storeName: storeData?.name || '',
			storeLocation: storeData?.address || '',
			days: (storeData?.workingDays || []) as VariantInterface[],
			hours: (storeData?.workingHours || []) as VariantInterface[],
		},
		onSubmit: async (values) => {
			const coordinates = storeData?.location?.coordinates;
			const lat = storeLocation?.latitude || coordinates?.[1];
			const lng = storeLocation?.longitude || coordinates?.[0];

			const body: CreateStoresParams = {
				address: values.storeLocation,
				lat: lat!,
				long: lng!,
				name: values.storeName,
				countryCode:
					storeLocation?.store?.countryCode ||
					storeLocation?.countryCode ||
					storeData?.countryCode,
				workingDays: pickupDays?.map((p) => ({ ...p, days: p.submenu })),
				workingHours: workHours?.map((p) => ({ ...p, days: p.submenu })),
			};

			try {
				if (isEditing) {
					await ProductService.editProductStores(storeData.id, {
						...body,
					});
					toastVariant('Store updated successfully', false);
				} else {
					await ProductService.createProductStores({
						...body,
					});
					toastVariant('Store created successfully', false);
				}
				handleModalClose();
			} catch (error) {
				toastVariant(`Something went wrong. ${error?.toString()!}`, true);
			}
		},
		validationSchema: Yup.object({
			storeName: Yup.string().required('Store name is required'),
			storeLocation: Yup.string().required('Store location is required'),
			days: Yup.array().min(1, 'Working days are required'),
			hours: Yup.array().min(1, 'Working hours are required'),
		}),
	});

	const locationRef = useDetectClickOutside({
		onTriggered: () => setLocationResults(false),
	});

	const handleCheckedDays = (data: VariantInterface) => {
		setPickupDays([data]);
		if (data.variant === 'custom') {
			setWorkHours([data]);
		}
		formik.setFieldValue('days', [data]);
	};

	const handleCheckedHours = (data: VariantInterface) => {
		setWorkHours([data]);
		if (data.variant === 'custom') {
			setPickupDays([data]);
		}
		formik.setFieldValue('hours', [data]);
	};

	const handleModalClose = () => {
		setStoreLocation(undefined);
		formik.resetForm();
		onClose();
	};

	const onSelectLocation = async (location: Geo) => {
		setLocationResults(false);

		if (onClearReverseGeoFlag) {
			onClearReverseGeoFlag();
		}

		try {
			const { data } = await GeoService.getLocationDetails({
				locationid: location.locationId!,
			});

			const addressLabel = data.address?.label || location.label;

			formik.setFieldValue('storeLocation', addressLabel);

			setStoreLocation({
				...location,
				latitude: data.Latitude,
				longitude: data.Longitude,
				address: data.address,
				countryCode: data.address?.countryCode || location.countryCode,
				label: addressLabel,
			});
		} catch (error) {
			toastVariant(
				`Failed to fetch location details: ${error?.toString()}`,
				true,
			);
		}
	};

	useEffect(() => {
		if (storeData?.workingDays?.length) {
			setPickupDays(storeData?.workingDays as VariantInterface[]);
		}
		if (storeData?.workingHours?.length) {
			setWorkHours(storeData?.workingHours as VariantInterface[]);
		}
	}, [storeData]);

	useEffect(() => {
		if (
			storeLocation &&
			(storeLocation.label || storeLocation.address?.label)
		) {
			const addressLabel = storeLocation.label || storeLocation.address?.label;

			if (formik.values.storeLocation !== addressLabel) {
				formik.setFieldValue('storeLocation', addressLabel);
			}

			setLocationResults(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeLocation]);

	const handleClear = () => {
		formik.setFieldValue('storeLocation', '');
		setStoreLocation(undefined);
		if (onClearReverseGeoFlag) {
			onClearReverseGeoFlag();
		}
	};

	const renderFormField = (
		label: string,
		name: TextFieldName,
		placeholder: string,
		required = true,
	) => (
		<SypacInput
			error={!!(formik.touched[name] && formik.errors[name])}
			className="w-full"
		>
			<SypacText className="mb-2" variant="overline-normal-large">
				<p>
					{label} {required && <span className="text-red">*</span>}
				</p>
			</SypacText>
			<input
				className={cn(
					'w-full py-[11px] pl-3 border rounded-lg placeholder:text-gray-22 placeholder:text-base transition',
					formik.touched[name] && formik.errors[name]
						? 'border-red-orange'
						: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue',
					name === 'storeLocation' && 'pl-[40px]',
				)}
				name={name}
				type="text"
				placeholder={placeholder}
				value={formik.values[name]}
				onChange={(event) => {
					formik.handleChange(event);
					if (name === 'storeName') {
						setStoreName(event.target.value);
					}
					if (name === 'storeLocation') {
						onChange(event.target.value);
					}
				}}
				onFocus={() => {
					if (name === 'storeLocation') {
						if (!isReverseGeoLocation && locations?.length > 0) {
							setLocationResults(true);
						}
					}
				}}
			/>
			{formik.touched[name] && formik.errors[name] && (
				<span className="input-error">{formik.errors[name]?.toString()}</span>
			)}
		</SypacInput>
	);

	const LocationDropdown = ({ locations, visible }: LocationDropdownProps) => {
		if (!visible || !locations?.length || isReverseGeoLocation) return null;

		return (
			<div
				className={`absolute top-[78px] w-full flex-col shadow-2xl rounded-lg max-h-72 overflow-hidden bg-white z-50 ${
					locationResults ? 'flex' : 'hidden'
				}`}
			>
				{locations?.length ? (
					<div className="flex flex-col w-full p-[3px] pr-3 overflow-y-scroll overflow-x-hidden">
						{locations.map((location: Geo) => (
							<div
								key={location.locationId}
								className="w-[471px] flex gap-3 items-start px-[6px] py-1 border border-solid border-white rounded-[8px] cursor-pointer hover:bg-[#FAFAFA] hover:border-[#E8E8E8]"
								onClick={() => onSelectLocation(location)}
							>
								<StoreSearchPin />

								<div className="flex flex-col gap-[6px]">
									<SypacText
										variant="body-normal-medium"
										className="text-[16px] leading-[22px] text-gray-80"
									>
										<span>
											<HighlightText
												text={addressWithoutCityCountry(location.address)}
												query={formik.values.storeLocation}
												type="noCity"
												highlightedText={(location as any).highlightedText}
											/>
										</span>
									</SypacText>

									<SypacText
										variant="body-regular-small"
										className="text-[12px] leading-[16px] text-gray-40"
									>
										<span>
											<HighlightText
												text={cityCountryOnly(location.address)}
												query={formik.values.storeLocation}
												type="withCity"
												highlightedText={(location as any).highlightedText}
											/>
										</span>
									</SypacText>
								</div>
							</div>
						))}
					</div>
				) : null}
			</div>
		);
	};

	return (
		<section className="flex flex-col gap-5 justify-center">
			<header className="flex flex-col gap-6 px-3 xl-2xl:px-5 mb-2 xl-2xl:mb-10">
				<SypacText variant=" heading-4">
					<p className="text-gray-90">
						{isEditing ? (
							<T keyName="newStoreCreate.editStore">Edit store</T>
						) : (
							<T keyName="newStoreCreate.addNewStore">Add new store</T>
						)}
					</p>
				</SypacText>
			</header>

			<form
				onSubmit={formik.handleSubmit}
				className="sm:w-[432px] xl-2xl:w-[544px] flex flex-col border border-solid border-gray-10 rounded-xl mx-3 xl-2xl:mx-5 bg-white box-border"
			>
				<div className="flex flex-col p-3 gap-3">
					<div className="flex bg-alabaster border border-solid border-gray-10 rounded-10 p-3">
						{renderFormField(
							t('newStoreCreate.storeName', 'Store name'),
							'storeName',
							t('newStoreCreate.enterStoreName', 'Enter store name'),
						)}
					</div>

					<div className="flex flex-col gap-2 bg-alabaster border border-solid border-gray-10 rounded-10 p-3">
						<div ref={locationRef} className="relative">
							{renderFormField(
								t('newStoreCreate.storeLocation', 'Store location'),
								'storeLocation',
								t('newStoreCreate.enterStoreLocation', 'Enter store location'),
							)}

							<span className="absolute top-[32px] left-3">
								<StoreSearchPin isActive={locationResults} />
							</span>

							<div className="absolute top-[32px] right-[20px] flex justify-center items-center gap-3">
								<SypacIcon
									icon-name="Magnifer"
									className="text-gray-40"
									size="custom"
									width="24px"
									height="24px"
								/>

								{formik.values.storeLocation && (
									<>
										<div className="w-[1px] h-[12px] bg-gray-10" />
										<SypacButton variant="subTitle">
											<button
												type="button"
												className="w-[18px] h-[18px] p-0 m-0 flex justify-center items-center border border-solid border-gray-40 rounded-[4px]"
												onClick={handleClear}
											>
												<Close color="#919191" width="6" height="6" />
											</button>
										</SypacButton>
									</>
								)}
							</div>

							<LocationDropdown
								locations={locations}
								visible={locationResults}
							/>
						</div>
					</div>

					<div className="flex flex-col bg-alabaster border border-solid border-gray-10 rounded-10 p-3">
						<SypacText className="mb-2" variant="overline-normal-large">
							<p>
								<T keyName="newStoreCreate.workingDays">Working days</T>{' '}
								<span className="text-red">*</span>
							</p>
						</SypacText>
						<DaysSelect
							daysMenu={daysMenu}
							pickupDays={pickupDays}
							currentDays={formik.values.days}
							workHours={workHours}
							handleCheckedDays={handleCheckedDays}
							isError={!!(formik?.touched.days && formik?.errors.days)}
						/>
						{formik?.touched.days && formik?.errors.days ? (
							<span className="input-error">
								{formik.errors.days.toString()}
							</span>
						) : null}
					</div>

					<div className="flex flex-col bg-alabaster border border-solid border-gray-10 rounded-10 p-3">
						<SypacText className="mb-2" variant="overline-normal-large">
							<p>
								<T keyName="newStoreCreate.workingHours">Working hours</T>{' '}
								<span className="text-red">*</span>
							</p>
						</SypacText>
						<HoursSelect
							hoursMenu={hoursMenu}
							pickupDays={pickupDays}
							currentHours={formik.values.hours}
							workHours={workHours}
							handleCheckedHours={handleCheckedHours}
							isError={!!(formik?.touched.hours && formik?.errors.hours)}
						/>
						{formik?.touched.hours && formik?.errors.hours ? (
							<span className="input-error">
								{formik.errors.hours.toString()}
							</span>
						) : null}
					</div>
				</div>

				<footer className="mt-auto flex w-full py-2.5 xl-2xl:py-4 gap-4 [&_button]:transition border border-solid border-b-0 rounded-10 border-gray-10 -mx-[1px]">
					<SypacButton
						variant="secondary"
						size="small"
						className="w-full pl-2.5 xl-2xl:pl-4"
					>
						<button
							type="button"
							className="w-full py-2.5 rounded-lg"
							onClick={handleModalClose}
						>
							<SypacText variant="body-regular-medium">
								<p>
									<T keyName="fleet.close">Close</T>
								</p>
							</SypacText>
						</button>
					</SypacButton>
					<SypacButton
						variant="primary"
						size="small"
						className="w-full pr-2.5 xl-2xl:pr-4"
					>
						<button type="submit" className="w-full py-2.75 rounded-lg">
							<SypacText variant="body-regular-medium">
								<p className="ml-3 text-white">
									{isEditing ? (
										<T keyName="newStoreCreate.editStore">Edit store</T>
									) : (
										<T keyName="productStores.createStore">Create store</T>
									)}
								</p>
							</SypacText>
						</button>
					</SypacButton>
				</footer>
			</form>

			<footer className="px-3 xl-2xl:px-5 mt-2 xl-2xl:mt-6">
				<SypacText variant="body-regular-medium">
					<p className="text-sm text-gray-40">
						<T keyName="fleet.havingIssues">Having issues?</T>{' '}
						<a
							className="no-underline text-cornflower-blue"
							href="mailto:support@sypac.app"
						>
							<T keyName="fleet.contactUs">Contact us.</T>
						</a>
					</p>
				</SypacText>
			</footer>
		</section>
	);
};

export default NewStoreCreate;
